import pxToRem from "hooks/usePxToRem";

import {Typography} from "@mui/material";
import {Grid} from "@mui/system";

import {CustomAutocomplete} from "ui";
import {ConfirmModal, CustomModal, DefaultCardStructure} from "components";
import {Controller, useFieldArray, useFormContext} from "react-hook-form";
import {useState} from "react";
import {ModalNewEquipment} from "./components";
import {ProcessEquipment} from "views/ProcessDetails/Process.types";
import {AutoCompleteData} from "ui/CustomAutoComplete/CustomAutoComplete";
import {
  DeleteLogisticEquipment,
  GetEquipmentsList,
  PostNewLogisticEquipment,
} from "services/api_v2/Processes/Processes.service";
import useChangeDAteAndAutocomplete from "views/ProcessDetails/hooks/useChangeDAteAndAutocomplete";
import useHandleFieldLists from "views/ProcessDetails/hooks/useHandleFieldLists";
import {getTooltipText} from "views/ProcessDetails/utils/getTooltipText";
import useLoading from "hooks/useLoading";
import useToast from "hooks/useToast";
import useAlertMessage from "hooks/useAlertMessage";
import ProcessTextField from "views/ProcessDetails/refactor/components/ProcessTextField";
import {Process} from "views/ProcessDetails/refactor/types/process.types";
import {useResolvedProcessId} from "views/ProcessDetails/refactor/contexts/ResolvedProcessIdContext";

type Props = {
  refetchProcess: () => Promise<Process | undefined>;
};

const LoadEquipment = (props: Props) => {
  const {refetchProcess} = props;
  const {control, setValue, getValues} = useFormContext();
  const processId = useResolvedProcessId();
  const {fields} = useFieldArray({
    name: "logisticEquipments",
    keyName: "equipmentFormId",
  });
  const [setLoading] = useLoading();
  const [showToast] = useToast();
  const [showAlert] = useAlertMessage();

  const [equipmentToRemove, setEquipmentToRemove] =
    useState<ProcessEquipment | null>(null);

  const handleCreateProcessEquipments = async (data: ProcessEquipment) => {
    try {
      setLoading(true);
      await PostNewLogisticEquipment({
        idLogisticProcess: processId,
        idEquipment: data.equipment?.id,
        quantity: data.quantity,
        freeTimeHouseDestination: data.freeTimeHouseDestination,
        freeTimeHouseOrigin: data.freeTimeHouseOrigin,
        freeTimeMasterDestination: data.freeTimeMasterDestination,
        freeTimeMasterOrigin: data.freeTimeMasterOrigin,
      });
      showToast("Equipamento vinculado com sucesso!", "success");
      await refetchProcess();
    } catch (error: any) {
      console.error(error);
      showAlert(error?.response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  const [modalIsOpen, setModalIsOpen] = useState(false);

  const handleCloseNewEquipmentModal = () => {
    setModalIsOpen(false);
  };

  const [confirmModalIsOpen, setConfirmModalIsOpen] = useState(false);

  const handleCloseConfirmModal = () => {
    setConfirmModalIsOpen(false);
    setEquipmentToRemove(null);
  };

  const handleConfirmEquipmentDeletion = async () => {
    if (!equipmentToRemove?.id) {
      showToast("Algo deu errado, tente novamente!");
      return;
    }

    try {
      setLoading(true);
      await DeleteLogisticEquipment(equipmentToRemove?.id);
      await refetchProcess();
    } catch (error: any) {
      console.error(error);
      showAlert(error?.response?.data?.message);
    } finally {
      setLoading(false);
    }
    handleCloseConfirmModal();
  };

  const [equipmentsOptions, setEquipmentsOptions] = useState(
    [] as AutoCompleteData[],
  );
  const [search, setSearch] = useState<string>();

  const {
    getOptions: getEquipmentsOptions,
    onBottomScroll: onBottomScrollEquipments,
    isLoadingOptions: isLoadingEquipmentsOptions,
  } = useHandleFieldLists(GetEquipmentsList);

  const {handleChangeAutocomplete, mountOptions} =
    useChangeDAteAndAutocomplete();

  return (
    <DefaultCardStructure
      title="Equipamentos"
      hasDivider
      isSubcard
      //Não teremos adição de equipamentos por motivos de alteração de taxas.
    >
      {confirmModalIsOpen && (
        <ConfirmModal
          isOpen={confirmModalIsOpen}
          title={`Tem certeza de que deseja excluir o equipamento ${equipmentToRemove?.nameEquipment}? `}
          subtitle={"Essa ação é definitiva e não pode ser revertida!"}
          confirmButtonFn={handleConfirmEquipmentDeletion}
          onClose={handleCloseConfirmModal}
        />
      )}
      {modalIsOpen && (
        <CustomModal
          isOpen={modalIsOpen}
          onClose={handleCloseNewEquipmentModal}
          title={"Inserir novo equipamento"}
          titleIcon={"add"}
        >
          <ModalNewEquipment
            handleCreateProcessEquipments={handleCreateProcessEquipments}
            onClose={handleCloseNewEquipmentModal}
          />
        </CustomModal>
      )}
      {fields.length === 0 && (
        <Typography>Não existem equipamentos no processo.</Typography>
      )}
      {fields.map((field, index) => (
        <Grid
          container
          rowSpacing={pxToRem(20)}
          columnSpacing={pxToRem(8)}
          alignItems="flex-end"
          mb={fields.length > 1 ? pxToRem(20) : 0}
          key={field.equipmentFormId}
        >
          <Grid size={3}>
            <Controller
              name={`logisticEquipments[${index}].equipment`}
              control={control}
              render={({field: {value}}) => (
                <CustomAutocomplete
                  fullWidth
                  textFieldProps={{
                    label: "Equipamento",
                  }}
                  name={`logisticEquipments[${index}].equipment`}
                  placeholder="Equipamento"
                  disabled
                  tooltipText={getTooltipText(value?.name)}
                  loading={isLoadingEquipmentsOptions}
                  options={mountOptions(value, equipmentsOptions)}
                  value={value?.name ?? ""}
                  onOpen={() =>
                    getEquipmentsOptions(setEquipmentsOptions, 1, search)
                  }
                  onInputChange={(e, value) => {
                    if (e !== null) {
                      setSearch(value);
                      getEquipmentsOptions(setEquipmentsOptions, 1, search);
                    }
                  }}
                  getNextPage={() => {
                    onBottomScrollEquipments(setEquipmentsOptions, search);
                  }}
                  onChange={(e, value) => {
                    handleChangeAutocomplete(
                      e,
                      value,
                      `logisticEquipments[${index}].equipment`,
                    );
                  }}
                  isOptionEqualToValue={(option, value) =>
                    value === option?.label
                  }
                />
              )}
            />
          </Grid>
          <Grid size={1}>
            <Controller
              name={`logisticEquipments[${index}].quantity`}
              control={control}
              render={({field: {value}}) => (
                <ProcessTextField
                  name={`logisticEquipments[${index}].quantity`}
                  type="number"
                  placeholder="Quantidade"
                  value={value}
                  onChange={(e) => {
                    setValue(
                      `logisticEquipments[${index}].quantity`,
                      +e.target.value,
                      {
                        shouldDirty: true,
                      },
                    );
                    setValue(
                      `totalOfEquipments`,
                      getValues(`totalOfEquipments`) + Number(e.target.value),
                    );
                  }}
                  size="small"
                  fullWidth
                  label="Quantidade"
                />
              )}
            />
          </Grid>
          <Grid size={2}>
            <Controller
              name={`logisticEquipments[${index}].freeTimeMasterOrigin`}
              control={control}
              render={({field: {value}}) => (
                <ProcessTextField
                  name={`logisticEquipments[${index}].freeTimeMasterOrigin`}
                  type="text"
                  disabled
                  placeholder="Origem"
                  value={value}
                  onChange={(e) => {
                    setValue(
                      `logisticEquipments[${index}].freeTimeMasterOrigin`,
                      +e.target.value,
                      {
                        shouldDirty: true,
                      },
                    );
                  }}
                  size="small"
                  fullWidth
                  label="FT Master Origem"
                />
              )}
            />
          </Grid>
          <Grid size={2}>
            <Controller
              name={`logisticEquipments[${index}].freeTimeHouseOrigin`}
              control={control}
              render={({field: {value}}) => (
                <ProcessTextField
                  name={`logisticEquipments[${index}].freeTimeHouseOrigin`}
                  type="text"
                  disabled
                  placeholder="Origem"
                  value={value}
                  onChange={(e) => {
                    setValue(
                      `logisticEquipments[${index}].freeTimeHouseOrigin`,
                      +e.target.value,
                      {
                        shouldDirty: true,
                      },
                    );
                  }}
                  size="small"
                  fullWidth
                  label="FT House Origem"
                />
              )}
            />
          </Grid>
          <Grid size={2}>
            <Controller
              name={`logisticEquipments[${index}].freeTimeMasterDestination`}
              control={control}
              render={({field: {value}}) => (
                <ProcessTextField
                  name={`logisticEquipments[${index}].freeTimeMasterDestination`}
                  type="text"
                  disabled
                  placeholder="Destino"
                  value={value}
                  onChange={(e) => {
                    setValue(
                      `logisticEquipments[${index}].freeTimeMasterDestination`,
                      +e.target.value,
                      {
                        shouldDirty: true,
                      },
                    );
                  }}
                  size="small"
                  fullWidth
                  label="FT Master Destino"
                />
              )}
            />
          </Grid>
          <Grid size={2}>
            <Controller
              name={`logisticEquipments[${index}].freeTimeHouseDestination`}
              control={control}
              render={({field: {value}}) => (
                <ProcessTextField
                  name={`logisticEquipments[${index}].freeTimeHouseDestination`}
                  type="text"
                  disabled
                  placeholder="Destino"
                  value={value}
                  onChange={(e) => {
                    setValue(
                      `logisticEquipments[${index}].freeTimeHouseDestination`,
                      +e.target.value,
                      {
                        shouldDirty: true,
                      },
                    );
                  }}
                  size="small"
                  fullWidth
                  label="FT House Destino"
                />
              )}
            />
          </Grid>
          {/*
          Não teremos remoção de equipamentos por motivos de alteração de
          taxas.*/}
        </Grid>
      ))}
    </DefaultCardStructure>
  );
};

export default LoadEquipment;
